//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

/**
 * WebhookDataService
 *
 * Service for managing Webhook registrations (CRUD + key operations).
 * Maps to the backend endpoints defined in webhooks/management_routes.py.
 *
 * Management endpoints: /api/v1/loops/{loop_id}/webhooks/*
 * Key generation:       /api/v1/webhooks/generate-key
 */

import axios from './axios';
import Q from 'q';
import async from 'async';
import { User } from 'oidc-client-ts';
import { ResponseType } from 'axios';
import { getSafeToken } from './securityUtils';

type JSONPrimitive = string | number | boolean;
type JSONObject = { [key: string]: JSONValue };
type JSONArray = JSONValue[];
type JSONValue = JSONPrimitive | JSONObject | JSONArray | undefined;

interface QueueOptions {
  method: string;
  url: string;
  body: object;
  headers: { [key: string]: string };
  queryParameters: { [key: string]: string };
  form: FormData;
  deferred: Q.Deferred<any>;
  responseType: ResponseType;
}

function serializeQueryParams(parameters: { [key: string]: string }) {
  const str = [];
  for (const p in parameters) {
    if (parameters.hasOwnProperty(p)) {
      str.push(encodeURIComponent(p) + '=' + encodeURIComponent(parameters[p]));
    }
  }
  return str.join('&');
}

// ── Types ────────────────────────────────────────────────────────────

// Output channel types supported by webhook v2. Empty/undefined means no channel.
export type OutputChannelType = 'slack' | 'google_chat' | 'teams';

export interface Webhook {
  id: string;
  name: string;
  description?: string | null;
  webhook_url: string;
  hmac_key: string;
  hmac_key_masked?: string;
  is_enabled: boolean;
  created_by?: string;
  created_at?: string;
  // v2 fields
  task_name?: string | null;
  output_channel_type?: OutputChannelType | null;
  integration_id?: string | null;
  // Slack output via an installed Slack connection (Settings → Integrations).
  slack_connection_id?: string | null;
  slack_channel_id?: string | null;
  slack_channel_name?: string | null;
  oauth_completed?: boolean;
  run_as_username?: string | null;
  verification_mode?: 'hmac' | 'none';
}

export interface WebhookCreatePayload {
  name: string;
  description?: string;
  hmac_key: string;
  // v2 fields
  task_name: string;
  output_channel_type?: OutputChannelType;
  // Google Chat / Teams: pre-configured integration.
  integration_id?: string;
  // Slack: installed connection + channel picked from the live channel list.
  slack_connection_id?: string;
  slack_channel_id?: string;
  slack_channel_name?: string;
}

/** An installed Slack connection (Settings → Integrations) selectable as output. */
export interface SlackConnectionOption {
  connection_id: string;
  name: string;
  status: string;
  has_bot_token: boolean;
}

/** A Slack channel the connection's bot can see, from conversations.list. */
export interface SlackChannelOption {
  id: string;
  name: string;
  is_private: boolean;
  is_member: boolean;
}

/** Raw Slack connection row as returned by the slack-connections API. */
interface SlackConnectionRaw {
  connection_id?: unknown;
  name?: unknown;
  status?: unknown;
  has_bot_token?: unknown;
}

/** Raw channel item as returned by the slack-connections channels API. */
interface SlackChannelRaw {
  id?: unknown;
  name?: unknown;
  is_private?: unknown;
  is_member?: unknown;
}

/** Create response — a Webhook plus the OAuth URL the caller must redirect to. */
export interface WebhookCreateResponse extends Webhook {
  oauth_url?: string | null;
}

export interface WebhookOAuthInitiateResponse {
  id: string;
  oauth_url: string;
}

/** A selectable task for the create form (flattened from the loop tasks API). */
export interface LoopTaskOption {
  id: string;
  name: string;
  description?: string;
}

/** Raw task item as returned by the loop tasks API (before flattening). */
interface LoopTaskRaw {
  id?: unknown;
  name?: unknown;
  description?: unknown;
}

/** Raw category/capability group from the loop tasks API. */
interface LoopTaskCategoryRaw {
  tasks?: LoopTaskRaw[];
}

/** Raw response shape from the loop tasks endpoint. */
interface ListLoopTasksRaw {
  categories?: LoopTaskCategoryRaw[];
  capabilities?: LoopTaskCategoryRaw[];
}

export interface WebhookUpdatePayload {
  name?: string;
  description?: string;
}

export interface WebhookDeleteResponse {
  deleted: boolean;
  id: string;
  name: string;
}

export interface WebhookRegenerateKeyResponse {
  id: string;
  hmac_key: string;
}

export interface WebhookGenerateKeyResponse {
  hmac_key: string;
}

/** A single delivery in the read-only history list (last 15). */
export interface WebhookEventSummary {
  webhook_event_id: string;
  event_type?: string | null;
  task_name?: string | null;
  status?: string | null;
  received_at?: string | null;
  completed_at?: string | null;
  channel_notification_status?: string | null;
  has_error?: boolean;
  // Phase 2 — terminal outcome for rejected/blocked deliveries
  http_status?: number | null;
  outcome_reason?: string | null;
}

// ── Service ──────────────────────────────────────────────────────────

class WebhookDataService {
  static instance: WebhookDataService;
  private readonly queueAPICalls: async.QueueObject<unknown>;
  private readonly analyticsAIUrl: string | undefined;

  constructor(
    getUserContext: () => User,
    getIsAuthenticated: () => boolean,
    analyticsAIUrl: string | undefined,
  ) {
    if (!WebhookDataService.instance) {
      WebhookDataService.instance = this;
    }

    this.analyticsAIUrl = analyticsAIUrl;
    this.queueAPICalls = async.queue((options: QueueOptions, callback) => {
      WebhookDataService.instance._request(
        options.method,
        options.url,
        options.body,
        options.headers,
        options.queryParameters,
        options.form,
        options.deferred,
        function () {
          callback();
        },
        getUserContext,
        getIsAuthenticated,
        analyticsAIUrl,
        options.responseType,
      );
    }, 3);
  }

  public _request(
    method: string,
    url: string,
    body: object,
    headers: { [key: string]: string },
    queryParameters: { [key: string]: string },
    form: FormData | object,
    deferred: Q.Deferred<any>,
    completeJob: () => void,
    getUserContext: () => User,
    getIsAuthenticated: () => boolean,
    baseUrl: string | undefined,
    responseType: ResponseType = 'json',
  ) {
    const user = getUserContext();
    const isAuthenticated = getIsAuthenticated();

    const queryParams =
      queryParameters && Object.keys(queryParameters).length
        ? serializeQueryParams(queryParameters)
        : null;
    const urlWithParams = url + (queryParams ? '?' + queryParams : '');
    if (!isAuthenticated) {
      deferred.reject('User is not logged in.');
      completeJob();
      return;
    }
    headers['Authorization'] = 'Bearer ' + getSafeToken(user.access_token);
    // GET requests carry no body, so Content-Type is dead weight: it adds header
    // bytes to every read and can trigger an unnecessary CORS preflight on
    // cross-origin calls. Strip it centrally so call sites don't have to remember.
    if (method.toUpperCase() === 'GET') {
      delete headers['Content-Type'];
    }
    if (body && !Object.keys(body).length && !Array.isArray(body)) {
      body = {};
    }
    axios({
      method,
      url: urlWithParams,
      baseURL: baseUrl,
      headers,
      responseType: responseType,
      data:
        method === 'POST' || method === 'PUT' || method === 'PATCH'
          ? body
          : undefined,
    })
      .then((response) => {
        deferred.resolve(response.data || response);
        completeJob();
      })
      .catch((error) => {
        deferred.reject(error.response);
        completeJob();
      });
  }

  public request<T>(
    method: string,
    url: string,
    body: object,
    headers: JSONValue,
    queryParameters: JSONValue,
    form: JSONValue,
    deferred: Q.Deferred<T>,
    responseType = 'json',
  ) {
    this.queueAPICalls.push({
      method: method,
      url: url,
      body: body,
      headers: headers,
      queryParameters: queryParameters,
      form: form,
      deferred: deferred,
      responseType: responseType,
    });
  }

  // ═══════════════════════════════════════════════════════════════════
  // Webhook Management APIs
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Generate a new HMAC key (not stored — used during creation flow).
   */
  generateKey(): Q.Promise<WebhookGenerateKeyResponse> {
    const deferred = Q.defer<WebhookGenerateKeyResponse>();
    const headers = { 'Content-Type': 'application/json' };
    this.request(
      'POST',
      '/api/v1/webhooks/generate-key',
      {},
      headers,
      {},
      {},
      deferred,
    );
    return deferred.promise;
  }

  /**
   * Create a new webhook for a loop.
   */
  createWebhook(
    loopId: string,
    teamspaceId: string,
    payload: WebhookCreatePayload,
  ): Q.Promise<WebhookCreateResponse> {
    const deferred = Q.defer<WebhookCreateResponse>();
    const headers = { 'Content-Type': 'application/json' };
    this.request(
      'POST',
      `/api/v1/loops/${loopId}/webhooks`,
      payload,
      headers,
      { teamspace_id: teamspaceId },
      {},
      deferred,
    );
    return deferred.promise;
  }

  /**
   * List all webhooks for a loop.
   */
  listWebhooks(loopId: string, teamspaceId: string): Q.Promise<Webhook[]> {
    const deferred = Q.defer<Webhook[]>();
    const headers = { 'Content-Type': 'application/json' };
    this.request(
      'GET',
      `/api/v1/loops/${loopId}/webhooks`,
      {},
      headers,
      { teamspace_id: teamspaceId },
      {},
      deferred,
    );
    return deferred.promise;
  }

  /**
   * Get a single webhook by ID.
   */
  getWebhook(
    loopId: string,
    webhookId: string,
    teamspaceId: string,
  ): Q.Promise<Webhook> {
    const deferred = Q.defer<Webhook>();
    const headers = { 'Content-Type': 'application/json' };
    this.request(
      'GET',
      `/api/v1/loops/${loopId}/webhooks/${webhookId}`,
      {},
      headers,
      { teamspace_id: teamspaceId },
      {},
      deferred,
    );
    return deferred.promise;
  }

  /**
   * Update a webhook's name/description.
   */
  updateWebhook(
    loopId: string,
    webhookId: string,
    payload: WebhookUpdatePayload,
    teamspaceId: string,
  ): Q.Promise<Webhook> {
    const deferred = Q.defer<Webhook>();
    const headers = { 'Content-Type': 'application/json' };
    this.request(
      'PUT',
      `/api/v1/loops/${loopId}/webhooks/${webhookId}`,
      payload,
      headers,
      { teamspace_id: teamspaceId },
      {},
      deferred,
    );
    return deferred.promise;
  }

  /**
   * Enable or disable HMAC signature verification for a webhook.
   * mode = 'hmac' (verify) | 'none' (skip verification — unsigned source tools).
   */
  setVerificationMode(
    loopId: string,
    webhookId: string,
    teamspaceId: string,
    mode: 'hmac' | 'none',
  ): Q.Promise<{ id: string; verification_mode: 'hmac' | 'none' }> {
    const deferred = Q.defer<{
      id: string;
      verification_mode: 'hmac' | 'none';
    }>();
    const headers = { 'Content-Type': 'application/json' };
    this.request(
      'PUT',
      `/api/v1/loops/${loopId}/webhooks/${webhookId}/verification`,
      { mode },
      headers,
      { teamspace_id: teamspaceId },
      {},
      deferred,
    );
    return deferred.promise;
  }

  /**
   * List the most recent deliveries (last 15) for a webhook — read-only
   * traceability. Newest first.
   */
  listWebhookEvents(
    loopId: string,
    webhookId: string,
    teamspaceId: string,
  ): Q.Promise<WebhookEventSummary[]> {
    const deferred = Q.defer<WebhookEventSummary[]>();
    this.request(
      'GET',
      `/api/v1/loops/${loopId}/webhooks/${webhookId}/events`,
      {},
      {},
      { teamspace_id: teamspaceId },
      {},
      deferred,
    );
    return deferred.promise;
  }

  /**
   * Delete a webhook.
   */
  deleteWebhook(
    loopId: string,
    webhookId: string,
    teamspaceId: string,
  ): Q.Promise<WebhookDeleteResponse> {
    const deferred = Q.defer<WebhookDeleteResponse>();
    const headers = { 'Content-Type': 'application/json' };
    this.request(
      'DELETE',
      `/api/v1/loops/${loopId}/webhooks/${webhookId}`,
      {},
      headers,
      { teamspace_id: teamspaceId },
      {},
      deferred,
    );
    return deferred.promise;
  }

  /**
   * Regenerate the HMAC key for a webhook.
   */
  regenerateKey(
    loopId: string,
    webhookId: string,
    teamspaceId: string,
  ): Q.Promise<WebhookRegenerateKeyResponse> {
    const deferred = Q.defer<WebhookRegenerateKeyResponse>();
    const headers = { 'Content-Type': 'application/json' };
    this.request(
      'POST',
      `/api/v1/loops/${loopId}/webhooks/${webhookId}/regenerate-key`,
      {},
      headers,
      { teamspace_id: teamspaceId },
      {},
      deferred,
    );
    return deferred.promise;
  }

  /**
   * Get a fresh OAuth authorization URL for a webhook (re-authorization). The
   * caller redirects the browser to the returned oauth_url.
   */
  oauthInitiate(
    loopId: string,
    webhookId: string,
    teamspaceId: string,
  ): Q.Promise<WebhookOAuthInitiateResponse> {
    const deferred = Q.defer<WebhookOAuthInitiateResponse>();
    const headers = { 'Content-Type': 'application/json' };
    this.request(
      'GET',
      `/api/v1/loops/${loopId}/webhooks/${webhookId}/oauth-initiate`,
      {},
      headers,
      { teamspace_id: teamspaceId },
      {},
      deferred,
    );
    return deferred.promise;
  }

  /**
   * List the tasks available for a loop, flattened into selectable options.
   * Each task's `id` is the value stored as the webhook's task_name (the
   * orchestrator resolves a task by its id).
   */
  listLoopTasks(
    teamspaceId: string,
    loopId: string,
  ): Q.Promise<LoopTaskOption[]> {
    const deferred = Q.defer<ListLoopTasksRaw>();
    const headers = { 'Content-Type': 'application/json' };
    this.request(
      'GET',
      `/teamspace/${teamspaceId}/loop/${loopId}/tasks`,
      {},
      headers,
      {},
      {},
      deferred,
    );
    // The tasks API groups tasks under `categories` (a.k.a. `capabilities`).
    // Flatten + de-duplicate by id.
    return deferred.promise.then((resp) => {
      const groups: LoopTaskCategoryRaw[] = Array.isArray(resp?.categories)
        ? resp.categories!
        : Array.isArray(resp?.capabilities)
          ? resp.capabilities!
          : [];
      const seen = new Set<string>();
      const options: LoopTaskOption[] = [];
      for (const group of groups) {
        const tasks: LoopTaskRaw[] = Array.isArray(group?.tasks)
          ? group.tasks!
          : [];
        for (const task of tasks) {
          const id = typeof task?.id === 'string' ? task.id : '';
          if (!id || seen.has(id)) continue;
          seen.add(id);
          options.push({
            id,
            name: typeof task?.name === 'string' ? task.name : id,
            description:
              typeof task?.description === 'string'
                ? task.description
                : undefined,
          });
        }
      }
      return options.sort((a, b) => a.name.localeCompare(b.name));
    });
  }

  /**
   * List the teamspace's installed Slack connections (Settings → Integrations)
   * for the Slack output-channel picker. Same endpoint the Integrations page
   * uses; rejects on failure so the dialog can disable Slack and explain why.
   */
  listSlackConnections(
    teamspaceId: string,
  ): Q.Promise<SlackConnectionOption[]> {
    const deferred = Q.defer<SlackConnectionRaw[]>();
    const headers = { 'Content-Type': 'application/json' };
    this.request(
      'GET',
      `/teamspaces/${encodeURIComponent(teamspaceId)}/slack-connections`,
      {},
      headers,
      {},
      {},
      deferred,
    );
    return deferred.promise.then((resp) => {
      const list: SlackConnectionRaw[] = Array.isArray(resp) ? resp : [];
      return list
        .filter((c) => c && typeof c.connection_id === 'string')
        .map((c) => ({
          connection_id: c.connection_id as string,
          name:
            typeof c.name === 'string' ? c.name : (c.connection_id as string),
          status: typeof c.status === 'string' ? c.status : 'unknown',
          has_bot_token: c.has_bot_token === true,
        }));
    });
  }

  /**
   * List the channels a Slack connection's bot can see (live from Slack).
   * `is_member` distinguishes channels the bot has joined from public channels
   * it can still post to without joining. Rejects on failure so the dialog can
   * offer a retry rather than showing a partial list.
   */
  listSlackChannels(
    teamspaceId: string,
    connectionId: string,
  ): Q.Promise<SlackChannelOption[]> {
    const deferred = Q.defer<SlackChannelRaw[]>();
    const headers = { 'Content-Type': 'application/json' };
    this.request(
      'GET',
      `/teamspaces/${encodeURIComponent(teamspaceId)}/slack-connections/${encodeURIComponent(connectionId)}/channels`,
      {},
      headers,
      {},
      {},
      deferred,
    );
    return deferred.promise.then((resp) => {
      const list: SlackChannelRaw[] = Array.isArray(resp) ? resp : [];
      return list
        .filter((ch) => ch && typeof ch.id === 'string')
        .map((ch) => ({
          id: ch.id as string,
          name: typeof ch.name === 'string' ? ch.name : (ch.id as string),
          is_private: ch.is_private === true,
          is_member: ch.is_member === true,
        }));
    });
  }
}

export default WebhookDataService;
